import DOMPurify from "dompurify";
import { marked } from "marked";

import { isMermaidLang, renderMermaidToPng, renderSvgToPngBlob } from "../sidepanel/mermaid";
import { tr } from "../i18n/i18n.svelte";

/**
 * Export the current conversation (whatever is currently rendered inside the
 * messages container) as a Word-compatible HTML file (.doc). SharePoint and
 * Word both open this fine; mermaid SVGs are rasterized to PNG so they show
 * up reliably regardless of Word version.
 *
 * Citations and source-list buttons are flattened to plain text — clicking
 * them in Word would do nothing anyway, so we lose no real information.
 */
export async function exportConversationAsDoc(
  messagesContainer: HTMLElement,
  title: string,
): Promise<void> {
  // Walk only the bubbles — skip placeholders, the live "thinking" indicator,
  // the error banner, etc.
  const bubbles = Array.from(
    messagesContainer.querySelectorAll<HTMLElement>(".bubble"),
  );
  if (bubbles.length === 0) return;
  await exportBubblesAsDoc(bubbles, title, { roleHeadings: true });
}

/**
 * Export a single assistant response bubble as a Word-compatible .doc file.
 * Same machinery as the full-conversation export, minus the per-turn role
 * headings (there's only one turn, so they'd be noise).
 */
export async function exportResponseAsDoc(
  bubble: HTMLElement,
  title: string,
): Promise<void> {
  await exportBubblesAsDoc([bubble], title, { roleHeadings: false });
}

async function exportBubblesAsDoc(
  bubbles: HTMLElement[],
  title: string,
  opts: { roleHeadings: boolean },
): Promise<void> {
  const cleanTitle = title.trim() || "Conversation";
  const dateLabel = new Date().toLocaleDateString(tr({ fr: "fr-CH", en: "en-GB" }), {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Cloning isolates us from later DOM mutations and lets us rewrite SVGs /
  // strip interactivity without touching the UI.
  const wrapper = document.createElement("div");
  for (const b of bubbles) {
    wrapper.appendChild(b.cloneNode(true) as HTMLElement);
  }

  // Expand <details> ("Passages consultés") so the source list is visible
  // when the file is opened — a collapsed <details> in Word just shows the
  // summary and hides the rest.
  for (const d of wrapper.querySelectorAll<HTMLDetailsElement>("details")) {
    d.setAttribute("open", "");
  }

  // Mermaid toolbars and the per-message copy/download bar are interactive-only;
  // remove them. Same for the activity log (searches, reasoning): working
  // notes, not part of the answer.
  for (const t of wrapper.querySelectorAll<HTMLElement>(
    ".mermaid-toolbar, .msg-actions, .agent-activity, .file-card-head",
  )) {
    t.remove();
  }

  // Source-row buttons → plain spans (Word can't follow them anyway).
  for (const b of wrapper.querySelectorAll<HTMLButtonElement>(
    "button.source-row",
  )) {
    const span = document.createElement("span");
    span.className = "source-row";
    span.innerHTML = b.innerHTML;
    b.replaceWith(span);
  }

  // Citation pills → keep the visible "[N]" but drop the click affordance.
  for (const c of wrapper.querySelectorAll<HTMLElement>(".citation")) {
    c.removeAttribute("data-refs");
    c.removeAttribute("data-quote");
    c.removeAttribute("title");
  }

  // Replace each rendered Mermaid SVG with a PNG <img>. Done sequentially —
  // each conversion is fast and parallelism here would just queue on the
  // single main-thread canvas anyway.
  const svgs = Array.from(wrapper.querySelectorAll<SVGSVGElement>("svg"));
  for (const svg of svgs) {
    const result = await renderSvgToPngBlob(svg);
    if (!result) continue;
    const dataUrl = await blobToDataUrl(result.blob);
    const img = document.createElement("img");
    img.src = dataUrl;
    img.setAttribute("width", String(Math.round(result.width)));
    img.setAttribute("height", String(Math.round(result.height)));
    svg.replaceWith(img);
  }

  // For Mermaid blocks where rendering hasn't happened yet (rare — only if
  // the user exports immediately on load), fall back to keeping the source
  // as a code block. Nothing to do: the original <pre><code> is already in
  // the clone since mermaid.ts only replaces <pre> on successful render.

  // Add a role label above each bubble so the conversation reads naturally
  // when someone scans the document in Word. Uses a heading so SharePoint's
  // navigation pane lists each turn.
  if (opts.roleHeadings) {
    const role = (el: HTMLElement): "user" | "assistant" | "other" =>
      el.classList.contains("user")
        ? "user"
        : el.classList.contains("assistant")
          ? "assistant"
          : "other";
    for (const b of Array.from(wrapper.children) as HTMLElement[]) {
      const r = role(b);
      if (r === "other") continue;
      const heading = document.createElement("h3");
      heading.className = `role-heading role-${r}`;
      heading.textContent =
        r === "user" ? "Question" : tr({ fr: "Réponse", en: "Answer" });
      b.parentNode?.insertBefore(heading, b);
    }
  }

  const html = buildWordHtml(cleanTitle, dateLabel, wrapper.innerHTML);
  triggerDownload(
    `${sanitizeFilename(cleanTitle)} - ${dateForFilename()}.doc`,
    html,
  );
}

/**
 * Fichier .doc demandé par le modèle (bloc ```file:x.docx) : son markdown,
 * mis en forme comme une réponse exportée, sans l'en-tête « Conversation
 * exportée le… » — le document porte son propre titre. Les diagrammes
 * Mermaid deviennent des PNG, comme dans l'export de conversation.
 */
export async function markdownToWordDoc(markdown: string, title: string): Promise<Blob> {
  const wrapper = document.createElement("div");
  wrapper.className = "text assistant";
  wrapper.innerHTML = DOMPurify.sanitize(marked.parse(markdown, { async: false }) as string);
  for (const code of Array.from(wrapper.querySelectorAll<HTMLElement>("pre > code"))) {
    const lang = Array.from(code.classList).find((c) => c.startsWith("language-"))?.slice("language-".length) ?? "";
    if (!isMermaidLang(lang)) continue;
    const png = await renderMermaidToPng(code.textContent ?? "");
    if (!png) continue;
    const img = document.createElement("img");
    img.src = await blobToDataUrl(png.blob);
    img.setAttribute("width", String(Math.round(Math.min(png.width, 600))));
    img.setAttribute("height", String(Math.round(png.height * Math.min(1, 600 / png.width))));
    code.parentElement?.replaceWith(img);
  }
  const html = buildWordHtml(title, null, wrapper.outerHTML);
  return new Blob(["﻿" + html], { type: "application/msword" });
}

function buildWordHtml(title: string, dateLabel: string | null, body: string): string {
  const safeTitle = escapeHtml(title);
  // The xmlns:o / xmlns:w declarations + <xml> Word block are what makes
  // Word treat this HTML as a real document instead of a webpage. Without
  // them you still get "open in Word" but page layout is fragile.
  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="UTF-8">
  <title>${safeTitle}</title>
  <!--[if gte mso 9]>
  <xml>
    <w:WordDocument>
      <w:View>Print</w:View>
      <w:Zoom>100</w:Zoom>
      <w:DoNotOptimizeForBrowser/>
    </w:WordDocument>
  </xml>
  <![endif]-->
  <style>
    @page Section1 { size: 21cm 29.7cm; margin: 2cm 2cm 2cm 2cm; }
    div.Section1 { page: Section1; }
    body { font-family: Calibri, "Segoe UI", Arial, sans-serif; font-size: 11pt; color: #1a1a1a; line-height: 1.4; }
    h1 { font-size: 18pt; margin: 0 0 4pt; }
    h1.doc-title { color: #1a1a1a; }
    p.doc-meta { color: #666; font-size: 10pt; margin: 0 0 18pt; }
    h3.role-heading { font-size: 12pt; margin: 14pt 0 4pt; color: #2563eb; }
    h3.role-user { color: #1e40af; }
    h3.role-assistant { color: #047857; }
    .bubble { padding: 6pt 8pt; border: 1pt solid #d4d4d4; margin: 0 0 8pt; }
    .bubble.user { background: #eff6ff; }
    .bubble.assistant { background: #ffffff; }
    .text.user { white-space: pre-wrap; }
    .text.assistant p { margin: 0 0 6pt; }
    .text.assistant ul, .text.assistant ol { margin: 4pt 0 6pt 24pt; padding: 0; }
    .text.assistant li { margin: 2pt 0; }
    .text.assistant h1, .text.assistant h2, .text.assistant h3, .text.assistant h4 {
      font-size: 12pt; font-weight: 600; margin: 8pt 0 4pt;
    }
    .text.assistant code { background: #f5f5f5; padding: 1pt 3pt; font-family: Consolas, monospace; font-size: 10pt; }
    .text.assistant pre { background: #f5f5f5; padding: 6pt; font-family: Consolas, monospace; font-size: 10pt; white-space: pre-wrap; }
    .text.assistant blockquote { border-left: 3pt solid #d4d4d4; margin: 6pt 0; padding: 0 0 0 8pt; color: #555; }
    .text.assistant strong { font-weight: 600; }
    .text.assistant em { font-style: italic; }
    .text.assistant img { max-width: 16cm; height: auto; }
    .citation { background: #dbeafe; color: #1e40af; padding: 0 3pt; font-size: 10pt; font-weight: 500; }
    .mermaid-block { margin: 6pt 0; border: 1pt solid #e5e5e5; padding: 6pt; background: #ffffff; }
    .mermaid-figure { text-align: center; }
    .sources { margin-top: 6pt; border-top: 1pt solid #e5e5e5; padding-top: 4pt; }
    .sources summary { font-size: 10pt; color: #666; font-weight: 600; }
    .sources ul { list-style: none; padding: 4pt 0 0 0; margin: 0; }
    .sources li { margin: 2pt 0; font-size: 10pt; }
    .source-row { display: block; color: #555; }
    .ref-num { color: #2563eb; font-weight: 600; margin-right: 4pt; }
    .ref-name { margin-right: 4pt; }
    .ref-score { color: #888; }
  </style>
</head>
<body>
<div class="Section1">
  ${dateLabel === null ? "" : `<h1 class="doc-title">${safeTitle}</h1>
  <p class="doc-meta">${tr({ fr: "Conversation exportée le", en: "Conversation exported on" })} ${escapeHtml(dateLabel)} — Personal RAG / DPO Agent</p>`}
  ${body}
</div>
</body>
</html>`;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function triggerDownload(filename: string, html: string): void {
  // application/msword tells the browser/OS to hand the file to Word; .doc
  // extension keeps SharePoint happy.
  const blob = new Blob(["﻿" + html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80) || "Conversation";
}

function dateForFilename(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
